import client from '@/services/http/client'
import { isMockEnabled, mockDelay, paginate } from '@/services/http/mock'
import type {
  AppUserItem,
  AppUserPage,
  AppUserPasswordResetPayload,
  AppUserSessionRevokeResult,
  AppUserUpsertPayload,
  AppUserZentaoSyncResult,
} from '@/types/user'

const mockUsers: AppUserItem[] = [
  {
    id: 1,
    account: 'ops_admin',
    display_name: '运营管理员',
    email: '',
    status: 'active',
    source: 'dev_header',
    is_active: true,
    roles: ['admin', 'service_agent'],
    group_scopes: [],
    project_scopes: [],
    external_accounts: [],
    must_change_password: false,
    last_login_at: null,
    locked_until: null,
    failed_login_count: 0,
    created_at: '2026-05-31T00:00:00Z',
    updated_at: '2026-05-31T00:00:00Z',
  },
]

export async function fetchUsers(params: {
  page?: number
  page_size?: number
  keyword?: string
  status?: string
}): Promise<AppUserPage> {
  if (isMockEnabled()) {
    await mockDelay()
    const keyword = (params.keyword || '').trim()
    let filtered = [...mockUsers]
    if (keyword) {
      filtered = filtered.filter((item) =>
        [item.account, item.display_name, item.email || ''].some((value) => value.includes(keyword)),
      )
    }
    if (params.status) {
      filtered = filtered.filter((item) => item.status === params.status)
    }
    return paginate(filtered, params.page ?? 1, params.page_size ?? 20)
  }

  const { data } = await client.get<AppUserPage>('/users', { params })
  return data
}

export async function createUser(payload: AppUserUpsertPayload): Promise<AppUserItem> {
  if (isMockEnabled()) {
    await mockDelay()
    const now = new Date().toISOString()
    const item: AppUserItem = {
      id: Math.max(0, ...mockUsers.map((user) => user.id)) + 1,
      account: payload.account || '',
      display_name: payload.display_name || payload.account || '',
      email: payload.email || '',
      status: payload.status || 'active',
      source: 'dev_header',
      is_active: payload.is_active ?? payload.status !== 'disabled',
      roles: payload.roles || [],
      group_scopes: payload.group_scopes || [],
      project_scopes: payload.project_scopes || [],
      external_accounts: payload.external_accounts || [],
      must_change_password: false,
      last_login_at: null,
      locked_until: null,
      failed_login_count: 0,
      created_at: now,
      updated_at: now,
    }
    mockUsers.unshift(item)
    return item
  }

  const { data } = await client.post<AppUserItem>('/users', payload)
  return data
}

export async function updateUser(userId: number, payload: AppUserUpsertPayload): Promise<AppUserItem> {
  if (isMockEnabled()) {
    await mockDelay()
    const item = mockUsers.find((user) => user.id === userId)
    if (!item) throw new Error('用户不存在')
    Object.assign(item, payload, { updated_at: new Date().toISOString() })
    return item
  }

  const { data } = await client.put<AppUserItem>(`/users/${userId}`, payload)
  return data
}

export async function resolveZentaoAccount(displayName: string): Promise<AppUserZentaoSyncResult> {
  if (isMockEnabled()) {
    await mockDelay()
    const name = displayName.trim()
    if (!name) return { sync: { status: 'no_display_name', message: 'display_name is required before syncing zentao account' } }
    return {
      sync: {
        status: 'matched',
        external_account: name.toLowerCase().replace(/\s+/g, '_'),
        external_name: name,
        message: 'matched by exact realname',
      },
      metadata_sync: {},
    }
  }

  const { data } = await client.post<AppUserZentaoSyncResult>('/users/zentao-account/resolve', {
    display_name: displayName,
    refresh: true,
  })
  return data
}

export async function syncUserZentaoAccount(userId: number): Promise<AppUserZentaoSyncResult> {
  if (isMockEnabled()) {
    await mockDelay()
    const item = mockUsers.find((user) => user.id === userId)
    if (!item) throw new Error('用户不存在')
    const existing = item.external_accounts.find((account) => account.provider === 'zentao')
    if (existing) {
      return {
        sync: {
          status: 'unchanged',
          external_account: existing.external_account,
          external_name: existing.external_name,
          message: 'existing zentao account is already current',
        },
        metadata_sync: {},
        user: { ...item },
      }
    }
    const externalAccount = item.display_name.toLowerCase().replace(/\s+/g, '_')
    item.external_accounts = [
      { provider: 'zentao', external_account: externalAccount, external_name: item.display_name },
    ]
    item.updated_at = new Date().toISOString()
    return {
      sync: {
        status: 'created',
        external_account: externalAccount,
        external_name: item.display_name,
        message: 'zentao account linked by exact realname',
      },
      metadata_sync: {},
      user: { ...item },
    }
  }

  const { data } = await client.post<AppUserZentaoSyncResult>(`/users/${userId}/zentao-account/sync`)
  return data
}

export async function resetUserPassword(
  userId: number,
  payload: AppUserPasswordResetPayload,
): Promise<AppUserItem> {
  if (isMockEnabled()) {
    await mockDelay()
    const item = mockUsers.find((user) => user.id === userId)
    if (!item) throw new Error('用户不存在')
    Object.assign(item, {
      must_change_password: payload.must_change_password,
      failed_login_count: 0,
      locked_until: null,
      updated_at: new Date().toISOString(),
    })
    return item
  }

  const { data } = await client.post<AppUserItem>(`/users/${userId}/password/reset`, payload)
  return data
}

export async function unlockUser(userId: number): Promise<AppUserItem> {
  if (isMockEnabled()) {
    await mockDelay()
    const item = mockUsers.find((user) => user.id === userId)
    if (!item) throw new Error('用户不存在')
    Object.assign(item, {
      status: item.status === 'locked' ? 'active' : item.status,
      is_active: item.status === 'locked' ? true : item.is_active,
      failed_login_count: 0,
      locked_until: null,
      updated_at: new Date().toISOString(),
    })
    return item
  }

  const { data } = await client.post<AppUserItem>(`/users/${userId}/unlock`)
  return data
}

export async function revokeUserSessions(userId: number): Promise<AppUserSessionRevokeResult> {
  if (isMockEnabled()) {
    await mockDelay()
    const item = mockUsers.find((user) => user.id === userId)
    if (!item) throw new Error('用户不存在')
    return { revoked_count: item.id === userId ? 1 : 0, user_id: userId }
  }

  const { data } = await client.post<AppUserSessionRevokeResult>(`/users/${userId}/sessions/revoke`)
  return data
}
